import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';

import { ComponentDesignTimestamps } from '@paragon/contract-library';
import {
  componentDesignsBatchLocked,
  componentDesignsDeletedWithInsertions,
  projectSynchronized,
} from '@paragon/layout-models';
import { isArray } from '@paragon/utilities';

import { receiveComponentDesigns, receiveRegenerateFromSettingsResponse } from './componentDesignsSlice';

export interface ComponentDesignTimestampsState {
  readonly [componentDesignGuid: string]: ComponentDesignTimestamps | undefined;
}

const initialState: ComponentDesignTimestampsState = {};

export const componentDesignTimestampsSlice = createSlice({
  name: 'componentDesignTimestamps',
  initialState,
  reducers: {
    setComponentDesignTimestamps: (
      state,
      action: PayloadAction<{
        timestamps: ComponentDesignTimestamps | readonly ComponentDesignTimestamps[];
        allowTimeTraveling?: boolean;
      }>,
    ) => {
      updateState(state, action.payload.timestamps, action.payload.allowTimeTraveling);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(componentDesignsBatchLocked, (state, action) => {
      updateState(state, action.payload.updatedSummariesAndTimestamps.timestamps);
    });

    builder.addCase(componentDesignsDeletedWithInsertions, (state, action) => {
      for (const componentDesignGuid of action.payload.guids) {
        delete state[componentDesignGuid];
      }
    });

    builder.addCase(projectSynchronized, (state, action) => {
      for (const syncPayload of Object.values(action.payload)) {
        updateState(state, syncPayload.incomingTimestamps);
      }
    });

    builder.addCase(receiveComponentDesigns, (state, action) => {
      updateState(state, action.payload.timestamps);
    });

    builder.addCase(receiveRegenerateFromSettingsResponse, (state, action) => {
      updateState(state, action.payload.timestamps);
    });
  },
});

function updateState(
  state: Draft<ComponentDesignTimestampsState>,
  timestamps: ComponentDesignTimestamps | readonly ComponentDesignTimestamps[],
  allowTimeTraveling?: boolean,
) {
  const applyTimestamps = (newTimestamps: ComponentDesignTimestamps) => {
    const { componentDesignGuid } = newTimestamps;
    const stateTimestamps = state[componentDesignGuid];

    if (!stateTimestamps) {
      state[componentDesignGuid] = newTimestamps;
      return;
    }

    if (allowTimeTraveling || new Date(stateTimestamps.lastUpdated) < new Date(newTimestamps.lastUpdated)) {
      stateTimestamps.lastUpdated = newTimestamps.lastUpdated;
    }

    if (
      !stateTimestamps.lastAnalyzed ||
      (newTimestamps.lastAnalyzed &&
        (allowTimeTraveling || new Date(stateTimestamps.lastAnalyzed) < new Date(newTimestamps.lastAnalyzed)))
    ) {
      stateTimestamps.lastAnalyzed = newTimestamps.lastAnalyzed;
      stateTimestamps.lastAnalysisHadErrors = newTimestamps.lastAnalysisHadErrors;
      stateTimestamps.lastAnalyzedCapacity = newTimestamps.lastAnalyzedCapacity;
    }
  };

  if (isArray(timestamps)) {
    timestamps.forEach(applyTimestamps);
  } else {
    applyTimestamps(timestamps);
  }
}

export const { setComponentDesignTimestamps } = componentDesignTimestampsSlice.actions;
export const componentDesignTimestamps = componentDesignTimestampsSlice.reducer;
